import React, { useEffect, useRef } from "react";
import { Box, TextField } from "@mui/material";

/**
 * Segmented 6 digit code input used by the two factor screens.
 * Handles arrow keys, backspace and pasting a full code.
 */
export default function OtpInput({
  value = "",
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  autoFocus = true,
  error = false,
}) {
  const inputsRef = useRef([]);

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus]);

  const digits = Array.from({ length }, (_, i) => value[i] || "");

  const emit = (next) => {
    onChange(next);
    if (next.length === length && onComplete) onComplete(next);
  };

  const handleChange = (index, inputValue) => {
    const typed = inputValue.replace(/\D/g, "");
    if (!typed) {
      /* Clearing the current box */
      const next = digits.map((d, i) => (i === index ? "" : d)).join("");
      emit(next.replace(/\s/g, ""));
      return;
    }

    /* Typing or pasting - fill forward from the current box */
    const nextDigits = [...digits];
    typed.split("").forEach((char, offset) => {
      if (index + offset < length) nextDigits[index + offset] = char;
    });

    emit(nextDigits.join("").slice(0, length));

    const focusIndex = Math.min(index + typed.length, length - 1);
    inputsRef.current[focusIndex]?.focus();
    inputsRef.current[focusIndex]?.select();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      const nextDigits = [...digits];
      nextDigits[index - 1] = "";
      emit(nextDigits.join(""));
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    event.preventDefault();
    emit(pasted.slice(0, length));
    inputsRef.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 0.75, sm: 1 },
        justifyContent: "center",
        width: "100%",
      }}
    >
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputsRef.current[index] = el)}
          value={digit}
          disabled={disabled}
          error={error}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          inputProps={{
            inputMode: "numeric",
            autoComplete: index === 0 ? "one-time-code" : "off",
            maxLength: length,
            "aria-label": `Digit ${index + 1}`,
            style: {
              textAlign: "center",
              fontSize: "22px",
              fontWeight: 600,
              padding: "10px 0",
            },
          }}
          sx={{ width: { xs: 40, sm: 48 } }}
        />
      ))}
    </Box>
  );
}
